import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './Analytics.module.css';
import { AuthContext } from '../Providers/AuthContext';
import GlobalHeader from '../GlobalHeader';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../services/firebase/firebaseConfig';

interface PieceStats {
  piece: string;
  icon: string;
  popularity: number;
  winRate: number;
  deathRate: number;
  killRate: number;
  survivalRate: number;
  advancementRate: number;
  pickRate: number;
  gamesPlayed: number;
}

interface KingStats {
  piece: string;
  icon: string;
  kingPickRate: number;
  winRateAsKing: number;
  wins: number;
  losses: number;
  raceVictoryRate: number;
  captureDeathRate: number;
  gamesAsKing: number;
}

interface GameStats {
  category: string;
  avgGameLength: number;
  avgMovesPerGame: number;
  avgCapturesPerGame: number;
  raceVictoryRate: number;
  captureVictoryRate: number;
  drawRate: number;
  gamesPlayed: number;
}

type TabType = 'pieces' | 'kings' | 'games';

// Filter types
type TimeframeFilter = 'last30days' | 'last365days' | 'alltime';
type EloRangeFilter = 'all' | 'unranked' | 'below1000' | '1000-1200' | '1200-1400' | '1400-1600' | '1600-1800' | '1800-2000' | '2000plus';
type RegionFilter = 'all' | 'NA' | 'SA' | 'EU' | 'AF' | 'AS' | 'OC';
type ColorFilter = 'all' | 'light' | 'dark';

interface AnalyticsFilters {
  timeframe: TimeframeFilter;
  eloRange: EloRangeFilter;
  region: RegionFilter;
  color: ColorFilter;
}

// Real data from 12 alpha games - Piece Stats
// Popularity from all 12 games, Win rates from 5 completed games
const REAL_PIECE_STATS: PieceStats[] = [
  { piece: 'Bear', icon: '/Icons/Bear-light.png', popularity: 91.7, winRate: 66.7, deathRate: 0, killRate: 0, survivalRate: 0, advancementRate: 0, pickRate: 91.7, gamesPlayed: 11 },
  { piece: 'Crane', icon: '/Icons/Crane-light.png', popularity: 66.7, winRate: 100.0, deathRate: 0, killRate: 0, survivalRate: 0, advancementRate: 0, pickRate: 66.7, gamesPlayed: 8 },
  { piece: 'Frog', icon: '/Icons/Frog-light.png', popularity: 83.3, winRate: 60.0, deathRate: 0, killRate: 0, survivalRate: 0, advancementRate: 0, pickRate: 83.3, gamesPlayed: 10 },
  { piece: 'Fungi', icon: '/Icons/Fungi-light.png', popularity: 83.3, winRate: 42.9, deathRate: 0, killRate: 0, survivalRate: 0, advancementRate: 0, pickRate: 83.3, gamesPlayed: 10 },
  { piece: 'Goat', icon: '/Icons/Goat-light.png', popularity: 33.3, winRate: 0, deathRate: 0, killRate: 0, survivalRate: 0, advancementRate: 0, pickRate: 33.3, gamesPlayed: 4 },
  { piece: 'Monkey', icon: '/Icons/Monkey-light.png', popularity: 66.7, winRate: 20.0, deathRate: 0, killRate: 0, survivalRate: 0, advancementRate: 0, pickRate: 66.7, gamesPlayed: 8 },
  { piece: 'Octopus', icon: '/Icons/Octopus-light.png', popularity: 58.3, winRate: 100.0, deathRate: 0, killRate: 0, survivalRate: 0, advancementRate: 0, pickRate: 58.3, gamesPlayed: 7 },
  { piece: 'Penguin', icon: '/Icons/Penguin-light.png', popularity: 58.3, winRate: 33.3, deathRate: 0, killRate: 0, survivalRate: 0, advancementRate: 0, pickRate: 58.3, gamesPlayed: 7 },
  { piece: 'Rhino', icon: '/Icons/Rhino-light.png', popularity: 66.7, winRate: 100.0, deathRate: 0, killRate: 0, survivalRate: 0, advancementRate: 0, pickRate: 66.7, gamesPlayed: 8 },
  { piece: 'Snake', icon: '/Icons/Snake-light.png', popularity: 66.7, winRate: 0, deathRate: 0, killRate: 0, survivalRate: 0, advancementRate: 0, pickRate: 66.7, gamesPlayed: 8 },
  { piece: 'Tree', icon: '/Icons/Tree-light.png', popularity: 75.0, winRate: 33.3, deathRate: 0, killRate: 0, survivalRate: 0, advancementRate: 0, pickRate: 75.0, gamesPlayed: 9 },
  { piece: 'Virus', icon: '/Icons/Virus-light.png', popularity: 8.3, winRate: 0, deathRate: 0, killRate: 0, survivalRate: 0, advancementRate: 0, pickRate: 8.3, gamesPlayed: 1 },
  { piece: 'Wolf', icon: '/Icons/Wolf-light.png', popularity: 58.3, winRate: 50.0, deathRate: 0, killRate: 0, survivalRate: 0, advancementRate: 0, pickRate: 58.3, gamesPlayed: 7 }
];

// Real data from 12 alpha games - King Stats (Virus cannot be king)
// Pick rates from all 12 games, Win/Loss rates & victory types from 5 completed games
const REAL_KING_STATS: KingStats[] = [
  { piece: 'Penguin', icon: '/Icons/Penguin-light.png', kingPickRate: 20.8, winRateAsKing: 66.7, wins: 2, losses: 1, raceVictoryRate: 50.0, captureDeathRate: 0, gamesAsKing: 5 },
  { piece: 'Rhino', icon: '/Icons/Rhino-light.png', kingPickRate: 20.8, winRateAsKing: 100, wins: 2, losses: 0, raceVictoryRate: 50.0, captureDeathRate: 0, gamesAsKing: 5 },
  { piece: 'Crane', icon: '/Icons/Crane-light.png', kingPickRate: 16.7, winRateAsKing: 50, wins: 1, losses: 1, raceVictoryRate: 100.0, captureDeathRate: 100.0, gamesAsKing: 4 },
  { piece: 'Wolf', icon: '/Icons/Wolf-light.png', kingPickRate: 12.5, winRateAsKing: 0, wins: 0, losses: 0, raceVictoryRate: 0, captureDeathRate: 0, gamesAsKing: 3 },
  { piece: 'Bear', icon: '/Icons/Bear-light.png', kingPickRate: 8.3, winRateAsKing: 0, wins: 0, losses: 0, raceVictoryRate: 0, captureDeathRate: 0, gamesAsKing: 2 },
  { piece: 'Frog', icon: '/Icons/Frog-light.png', kingPickRate: 8.3, winRateAsKing: 0, wins: 0, losses: 2, raceVictoryRate: 0, captureDeathRate: 0, gamesAsKing: 2 },
  { piece: 'Fungi', icon: '/Icons/Fungi-light.png', kingPickRate: 4.2, winRateAsKing: 0, wins: 0, losses: 1, raceVictoryRate: 0, captureDeathRate: 100.0, gamesAsKing: 1 },
  { piece: 'Goat', icon: '/Icons/Goat-light.png', kingPickRate: 4.2, winRateAsKing: 0, wins: 0, losses: 0, raceVictoryRate: 0, captureDeathRate: 0, gamesAsKing: 1 },
  { piece: 'Monkey', icon: '/Icons/Monkey-light.png', kingPickRate: 4.2, winRateAsKing: 0, wins: 0, losses: 0, raceVictoryRate: 0, captureDeathRate: 0, gamesAsKing: 1 },
  { piece: 'Octopus', icon: '/Icons/Octopus-light.png', kingPickRate: 0, winRateAsKing: 0, wins: 0, losses: 0, raceVictoryRate: 0, captureDeathRate: 0, gamesAsKing: 0 },
  { piece: 'Snake', icon: '/Icons/Snake-light.png', kingPickRate: 0, winRateAsKing: 0, wins: 0, losses: 0, raceVictoryRate: 0, captureDeathRate: 0, gamesAsKing: 0 },
  { piece: 'Tree', icon: '/Icons/Tree-light.png', kingPickRate: 0, winRateAsKing: 0, wins: 0, losses: 0, raceVictoryRate: 0, captureDeathRate: 0, gamesAsKing: 0 }
];

// Game Stats - Single row for all games overview
const MOCK_GAME_STATS: GameStats[] = [
  { category: 'All Games', avgGameLength: 24.3, avgMovesPerGame: 48.6, avgCapturesPerGame: 3.2, raceVictoryRate: 37.8, captureVictoryRate: 62.2, drawRate: 0.0, gamesPlayed: 195380 }
];

// Light vs Dark comparison data
interface ColorComparisonStats {
  color: string;
  icon: string;
  avgGameLength: number;
  avgMovesPerGame: number;
  avgCapturesPerGame: number;
  firstCaptureRate: number;
  centerControlScore: number;
  raceVictoryRate: number;
  captureVictoryRate: number;
  drawRate: number;
  winRate: number;
  gamesPlayed: number;
}

const MOCK_COLOR_COMPARISON: ColorComparisonStats[] = [
  { color: 'Light', icon: '/Icons/Crane-light.png', avgGameLength: 23.8, avgMovesPerGame: 47.6, avgCapturesPerGame: 3.1, firstCaptureRate: 52.3, centerControlScore: 28.4, raceVictoryRate: 39.5, captureVictoryRate: 60.5, drawRate: 0.0, winRate: 51.2, gamesPlayed: 97690 },
  { color: 'Dark', icon: '/Icons/Crane-dark.png', avgGameLength: 24.8, avgMovesPerGame: 49.6, avgCapturesPerGame: 3.3, firstCaptureRate: 47.7, centerControlScore: 26.1, raceVictoryRate: 36.1, captureVictoryRate: 63.9, drawRate: 0.0, winRate: 48.8, gamesPlayed: 97690 }
];

function Analytics() {
  const navigate = useNavigate();
  const { isPremium, accountType, profileLoading } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState<TabType>('pieces');
  const [pieceStats, setPieceStats] = useState<PieceStats[]>(REAL_PIECE_STATS);
  const [kingStats, setKingStats] = useState<KingStats[]>(REAL_KING_STATS);
  const [gameStats, setGameStats] = useState<GameStats[]>(MOCK_GAME_STATS);
  const [colorComparison, setColorComparison] = useState<ColorComparisonStats[]>(MOCK_COLOR_COMPARISON);
  const [pieceSortCol, setPieceSortCol] = useState<keyof PieceStats>('popularity');
  const [kingSortCol, setKingSortCol] = useState<keyof KingStats>('kingPickRate');
  const [gameSortCol, setGameSortCol] = useState<keyof GameStats>('gamesPlayed');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [isExporting, setIsExporting] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [selectedExportMonth, setSelectedExportMonth] = useState<string>('');
  const [availableMonths, setAvailableMonths] = useState<Array<{month: string, count: number}>>([]);

  // Filter state
  const [filters, setFilters] = useState<AnalyticsFilters>({
    timeframe: 'alltime',
    eloRange: 'all',
    region: 'all',
    color: 'all'
  });

  // Show loading state while profile is being loaded
  if (profileLoading) {
    return (
      <div className={styles.container}>
        <GlobalHeader />
        <div className={styles.loading}>Loading...</div>
      </div>
    );
  }

  // TESTING: Analytics temporarily unlocked for all accounts
  // TODO: Re-enable premium check after testing
  // if (!isPremium || accountType !== 'real') {
  //   navigate('/main');
  //   return null;
  // }

  const handleSort = <T,>(column: keyof T, data: T[], setData: (data: T[]) => void) => {
    const newDirection = sortDirection === 'desc' ? 'asc' : 'desc';
    setSortDirection(newDirection);

    const sorted = [...data].sort((a, b) => {
      const aValue = a[column];
      const bValue = b[column];

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return newDirection === 'desc' ? bValue.localeCompare(aValue) : aValue.localeCompare(bValue);
      }

      return newDirection === 'desc'
        ? (bValue as number) - (aValue as number)
        : (aValue as number) - (bValue as number);
    });

    setData(sorted);
  };

  const getBarColor = (value: number, metric: string) => {
    if (metric === 'winRate' || metric === 'killRate' || metric === 'winRateAsKing') {
      if (value >= 55) return '#4ade80';
      if (value >= 50) return '#60a5fa';
      if (value >= 45) return '#fbbf24';
      return '#f87171';
    }

    if (metric === 'deathRate') {
      if (value >= 40) return '#f87171';
      if (value >= 35) return '#fbbf24';
      if (value >= 30) return '#60a5fa';
      return '#4ade80';
    }

    if (value >= 85) return '#4ade80';
    if (value >= 75) return '#60a5fa';
    if (value >= 65) return '#fbbf24';
    return '#f87171';
  };

  const renderSortIcon = (column: string) => {
    const currentCol = activeTab === 'pieces' ? pieceSortCol : activeTab === 'kings' ? kingSortCol : gameSortCol;
    if (currentCol !== column) return <span className={styles.sortIcon}>⇅</span>;
    return sortDirection === 'desc' ? <span className={styles.sortIcon}>▼</span> : <span className={styles.sortIcon}>▲</span>;
  };

  const totalPieceGames = pieceStats.reduce((sum, stat) => sum + stat.gamesPlayed, 0);
  const totalKingGames = kingStats.reduce((sum, stat) => sum + stat.gamesAsKing, 0);

  const openExportModal = async () => {
    try {
      // Fetch all games to count completed games per month
      const gamesCollection = collection(db, 'games');
      const gamesSnapshot = await getDocs(gamesCollection);

      const allGames = gamesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as any[];


      // Log a sample game to see its structure
      if (allGames.length > 0) {
      }

      // Count legacy games (completed games without timestamps)
      const legacyGameCount = allGames.filter((game: any) => {
        const isComplete = typeof game.lightWon === 'boolean';
        return isComplete && !game.timestamp;
      }).length;


      // Generate list of last 12 months with counts
      const currentDate = new Date();
      const monthsWithCounts: Array<{month: string, count: number}> = [];
      let oldestMonthIndex = -1;

      for (let i = 0; i < 12; i++) {
        const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
        const monthStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        const monthFormatted = monthStr.replace('-', ''); // YYYYMM

        // Count completed games for this month (only games with timestamps)
        const count = allGames.filter((game: any) => {
          const isComplete = typeof game.lightWon === 'boolean';
          if (!isComplete || !game.timestamp) return false;

          const gameDate = game.timestamp.toString(); // YYYYMMDD format
          const gameMonth = gameDate.substring(0, 6); // YYYYMM
          return gameMonth === monthFormatted;
        }).length;

        // Only include months with at least one completed game
        if (count > 0) {
          monthsWithCounts.push({ month: monthStr, count });
          oldestMonthIndex = monthsWithCounts.length - 1; // Track oldest month with games
        }
      }

      // Add legacy games to the oldest month with games
      if (legacyGameCount > 0 && oldestMonthIndex >= 0) {
        monthsWithCounts[oldestMonthIndex].count += legacyGameCount;
      }

      // If there are only legacy games and no timestamped games, add them to current month
      if (monthsWithCounts.length === 0 && legacyGameCount > 0) {
        monthsWithCounts.push({
          month: `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`,
          count: legacyGameCount
        });
      }


      if (monthsWithCounts.length === 0) {
        alert('⚠️ No completed games found in any month!');
        return;
      }

      setAvailableMonths(monthsWithCounts);
      setSelectedExportMonth(monthsWithCounts[0].month); // Default to most recent month with data
      setShowExportModal(true);
    } catch (error) {
      alert(`❌ Error loading month data: ${error}`);
    }
  };

  const handleExportGames = async () => {
    if (!selectedExportMonth) {
      alert('Please select a month to export');
      return;
    }

    setShowExportModal(false);
    setIsExporting(true);
    try {

      // Get the games collection
      const gamesCollection = collection(db, 'games');
      const gamesSnapshot = await getDocs(gamesCollection);


      // Convert to array of game data
      const allGames = gamesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as any[];

      // Find the oldest month with timestamped games
      const timestampedGames = allGames.filter((game: any) =>
        typeof game.lightWon === 'boolean' && game.timestamp
      );

      let oldestMonth = '';
      if (timestampedGames.length > 0) {
        const oldestTimestamp = Math.min(...timestampedGames.map((g: any) => g.timestamp));
        oldestMonth = oldestTimestamp.toString().substring(0, 6); // YYYYMM
      }

      const selectedMonthFormatted = selectedExportMonth.replace('-', ''); // Convert YYYY-MM to YYYYMM

      // If there are no timestamped games (all legacy), treat selected month as oldest
      const isOldestMonth = timestampedGames.length === 0 ? true : (oldestMonth === selectedMonthFormatted);


      // Filter to only include completed games from the selected month
      const monthGames = allGames.filter((game: any) => {
        // Game is complete if lightWon is a boolean (true or false)
        const isComplete = typeof game.lightWon === 'boolean';

        if (!isComplete) return false;

        // Check if game is from selected month
        if (game.timestamp) {
          const gameDate = game.timestamp.toString(); // YYYYMMDD format
          const gameMonth = gameDate.substring(0, 6); // YYYYMM
          return gameMonth === selectedMonthFormatted;
        }

        // Legacy games without timestamp - include them only if this is the oldest month
        if (isOldestMonth) {
          return true;
        }

        return false;
      });


      // Log details about why games were filtered
      if (monthGames.length === 0) {
      }

      if (monthGames.length === 0) {
        alert(`⚠️ No completed games found for ${selectedExportMonth}!`);
        setIsExporting(false);
        return;
      }

      // Create export object
      const exportData = {
        exportMonth: selectedExportMonth,
        exportDate: new Date().toISOString(),
        totalGames: monthGames.length,
        otherGamesFiltered: allGames.length - monthGames.length,
        games: monthGames
      };

      // Convert to JSON and download
      const jsonString = JSON.stringify(exportData, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = `zodiac_games_${selectedExportMonth}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      alert(`✅ Successfully exported ${monthGames.length} completed games from ${selectedExportMonth}!\n\nFilename: zodiac_games_${selectedExportMonth}.json\n\nStore in: /analytics/raw-data/${selectedExportMonth}/`);
    } catch (error) {
      alert(`❌ Error exporting games: ${error}`);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className={styles.container}>
      <GlobalHeader />
      <div className={styles.content}>
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <button
              className={styles.homeButton}
              onClick={() => navigate('/main')}
              title="Back to Home"
            >
              Home
            </button>
            <h1 className={styles.title}>Game Analytics</h1>
            {/* Tab Navigation */}
            <div className={styles.tabNavigation}>
              <button
                className={`${styles.tab} ${activeTab === 'pieces' ? styles.activeTab : ''}`}
                onClick={() => setActiveTab('pieces')}
              >
                Piece Statistics
              </button>
              <button
                className={`${styles.tab} ${activeTab === 'kings' ? styles.activeTab : ''}`}
                onClick={() => setActiveTab('kings')}
              >
                King Statistics
              </button>
              <button
                className={`${styles.tab} ${activeTab === 'games' ? styles.activeTab : ''}`}
                onClick={() => setActiveTab('games')}
              >
                Game Statistics
              </button>
            </div>
          </div>
          <div className={styles.headerRight}>
            <div className={styles.premiumBadge}>TESTING MODE</div>
            <button
              className={styles.exportButton}
              onClick={openExportModal}
              disabled={isExporting}
              title="Export all games from Firebase to JSON"
            >
              {isExporting ? 'Exporting...' : 'Export Game Data'}
            </button>
          </div>
        </div>

        {/* Piece Statistics Tab */}
        {activeTab === 'pieces' && (
          <>
            <div className={styles.statsOverview}>
              <div className={styles.statCard}>
                <div className={styles.statValue}>{totalPieceGames.toLocaleString()}</div>
                <div className={styles.statLabel}>Total Games</div>
              </div>
              <div className={styles.statCard}>
                <div className={styles.statValue}>
                  {(pieceStats.reduce((sum, s) => sum + s.winRate, 0) / pieceStats.length).toFixed(1)}%
                </div>
                <div className={styles.statLabel}>Avg Win Rate</div>
              </div>
              <div className={styles.statCard}>
                <div className={styles.statValue}>
                  {(pieceStats.reduce((sum, s) => sum + s.advancementRate, 0) / pieceStats.length).toFixed(1)} rows
                </div>
                <div className={styles.statLabel}>Avg Advancement</div>
              </div>
              <div className={styles.statCard}>
                <div className={styles.statValue}>
                  {(pieceStats.reduce((sum, s) => sum + s.killRate, 0) / pieceStats.length).toFixed(2)}
                </div>
                <div className={styles.statLabel}>Avg Kills/Game</div>
              </div>
              <div className={styles.statCard}>
                <div className={styles.statValue}>
                  {(pieceStats.reduce((sum, s) => sum + s.deathRate, 0) / pieceStats.length).toFixed(1)}%
                </div>
                <div className={styles.statLabel}>Avg Death Rate</div>
              </div>
            </div>

            <div className={styles.tableContainer}>
              <div className={styles.tableHeader}>
                <div className={styles.tableHeaderLeft}>
                  <h2>Piece Performance Data</h2>
                  <div className={styles.tableInfo}>Click column headers to sort</div>
                </div>

                {/* Inline Filter Dropdowns */}
                <div className={styles.tableHeaderFilters}>
                  <select
                    className={styles.inlineFilterSelect}
                    value={filters.timeframe}
                    onChange={(e) => setFilters({...filters, timeframe: e.target.value as TimeframeFilter})}
                  >
                    <option value="last30days">Last 30 Days</option>
                    <option value="last365days">Last 365 Days</option>
                    <option value="alltime">All Time</option>
                  </select>

                  <select
                    className={styles.inlineFilterSelect}
                    value={filters.eloRange}
                    onChange={(e) => setFilters({...filters, eloRange: e.target.value as EloRangeFilter})}
                  >
                    <option value="all">All Players</option>
                    <option value="unranked">Unranked - New Accounts</option>
                    <option value="below1000">&lt;1000 - Beginner</option>
                    <option value="1000-1200">1000-1200 - Developing</option>
                    <option value="1200-1400">1200-1400 - Intermediate</option>
                    <option value="1400-1600">1400-1600 - Advanced</option>
                    <option value="1600-1800">1600-1800 - Expert</option>
                    <option value="1800-2000">1800-2000 - Master</option>
                    <option value="2000plus">2000+ - Grandmaster</option>
                  </select>

                  <select
                    className={styles.inlineFilterSelect}
                    value={filters.region}
                    onChange={(e) => setFilters({...filters, region: e.target.value as RegionFilter})}
                  >
                    <option value="all">All Regions</option>
                    <option value="NA">North America</option>
                    <option value="SA">South America</option>
                    <option value="EU">Europe</option>
                    <option value="AF">Africa</option>
                    <option value="AS">Asia</option>
                    <option value="OC">Oceania</option>
                  </select>

                  <select
                    className={styles.inlineFilterSelect}
                    value={filters.color}
                    onChange={(e) => setFilters({...filters, color: e.target.value as ColorFilter})}
                  >
                    <option value="all">Both Colors</option>
                    <option value="light">Light</option>
                    <option value="dark">Dark</option>
                  </select>
                </div>
              </div>

              <div className={styles.tableWrapper}>
                <table className={styles.statsTable}>
                  <thead>
                    <tr>
                      <th className={styles.stickyColumn} onClick={() => handleSort('piece', pieceStats, setPieceStats)}>
                        Piece {renderSortIcon('piece')}
                      </th>
                      <th onClick={() => { setPieceSortCol('popularity'); handleSort('popularity', pieceStats, setPieceStats); }}>
                        Popularity {renderSortIcon('popularity')}
                        <span className={styles.columnHint}>Overall score</span>
                      </th>
                      <th onClick={() => { setPieceSortCol('winRate'); handleSort('winRate', pieceStats, setPieceStats); }}>
                        Win Rate {renderSortIcon('winRate')}
                        <span className={styles.columnHint}>% games won</span>
                      </th>
                      <th onClick={() => { setPieceSortCol('deathRate'); handleSort('deathRate', pieceStats, setPieceStats); }}>
                        Death Rate {renderSortIcon('deathRate')}
                        <span className={styles.columnHint}>% of games captured</span>
                      </th>
                      <th onClick={() => { setPieceSortCol('killRate'); handleSort('killRate', pieceStats, setPieceStats); }}>
                        Kill Rate {renderSortIcon('killRate')}
                        <span className={styles.columnHint}>Avg captures/game</span>
                      </th>
                      <th onClick={() => { setPieceSortCol('survivalRate'); handleSort('survivalRate', pieceStats, setPieceStats); }}>
                        Survival Rate {renderSortIcon('survivalRate')}
                        <span className={styles.columnHint}>% of game alive</span>
                      </th>
                      <th onClick={() => { setPieceSortCol('advancementRate'); handleSort('advancementRate', pieceStats, setPieceStats); }}>
                        Advancement Rate {renderSortIcon('advancementRate')}
                        <span className={styles.columnHint}>Avg rows advanced</span>
                      </th>
                      <th onClick={() => { setPieceSortCol('gamesPlayed'); handleSort('gamesPlayed', pieceStats, setPieceStats); }}>
                        Games {renderSortIcon('gamesPlayed')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {pieceStats.map((stat) => (
                      <tr key={stat.piece}>
                        <td className={styles.stickyColumn}>
                          <div className={styles.pieceCell}>
                            {filters.color === 'all' ? (
                              <div className={styles.pieceBothIcons}>
                                <img src={stat.icon.replace('-light', '-dark')} alt={`${stat.piece} Dark`} className={styles.pieceIconDark} />
                                <img src={stat.icon} alt={`${stat.piece} Light`} className={styles.pieceIconLight} />
                              </div>
                            ) : filters.color === 'dark' ? (
                              <img src={stat.icon.replace('-light', '-dark')} alt={stat.piece} className={styles.pieceIcon} />
                            ) : (
                              <img src={stat.icon} alt={stat.piece} className={styles.pieceIcon} />
                            )}
                            <span className={styles.pieceName}>{stat.piece}</span>
                          </div>
                        </td>
                        <td>
                          <div className={styles.barCell}>
                            <div className={styles.bar} style={{ width: `${stat.popularity}%`, backgroundColor: getBarColor(stat.popularity, 'popularity') }} />
                            <span className={styles.barValue}>{stat.popularity}</span>
                          </div>
                        </td>
                        <td>
                          <div className={styles.barCell}>
                            <div className={styles.bar} style={{ width: `${stat.winRate}%`, backgroundColor: getBarColor(stat.winRate, 'winRate') }} />
                            <span className={styles.barValue}>{stat.winRate}%</span>
                          </div>
                        </td>
                        <td>
                          <div className={styles.barCell}>
                            <div className={styles.bar} style={{ width: `${stat.deathRate}%`, backgroundColor: getBarColor(stat.deathRate, 'deathRate') }} />
                            <span className={styles.barValue}>{stat.deathRate}%</span>
                          </div>
                        </td>
                        <td>
                          <div className={styles.valueCell}>
                            <span className={styles.lifespanValue}>{stat.killRate.toFixed(2)}</span>
                            <span className={styles.lifespanUnit}>kills</span>
                          </div>
                        </td>
                        <td>
                          <div className={styles.barCell}>
                            <div className={styles.bar} style={{ width: `${stat.survivalRate}%`, backgroundColor: getBarColor(stat.survivalRate, 'survivalRate') }} />
                            <span className={styles.barValue}>{stat.survivalRate}%</span>
                          </div>
                        </td>
                        <td>
                          <div className={styles.valueCell}>
                            <span className={styles.lifespanValue}>{stat.advancementRate.toFixed(1)}</span>
                            <span className={styles.lifespanUnit}>rows</span>
                          </div>
                        </td>
                        <td><div className={styles.gamesCell}>{stat.gamesPlayed.toLocaleString()}</div></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* King Statistics Tab */}
        {activeTab === 'kings' && (
          <>
            <div className={styles.statsOverview}>
              <div className={styles.statCard}>
                <div className={styles.statValue}>{totalKingGames.toLocaleString()}</div>
                <div className={styles.statLabel}>Total King Picks</div>
              </div>
              <div className={styles.statCard}>
                <div className={styles.statValue}>Rhino 100%</div>
                <div className={styles.statLabel}>Best Win Rate</div>
              </div>
              <div className={styles.statCard}>
                <div className={styles.statValue}>Penguin & Rhino</div>
                <div className={styles.statLabel}>Most Popular (20.8%)</div>
              </div>
              <div className={styles.statCard}>
                <div className={styles.statValue}>5 / 12</div>
                <div className={styles.statLabel}>Completed Games</div>
              </div>
            </div>

            <div className={styles.tableContainer}>
              <div className={styles.tableHeader}>
                <div className={styles.tableHeaderLeft}>
                  <h2>King Performance Data</h2>
                  <div className={styles.tableInfo}>Click column headers to sort</div>
                </div>

                {/* Inline Filter Dropdowns */}
                <div className={styles.tableHeaderFilters}>
                  <select
                    className={styles.inlineFilterSelect}
                    value={filters.timeframe}
                    onChange={(e) => setFilters({...filters, timeframe: e.target.value as TimeframeFilter})}
                  >
                    <option value="last30days">Last 30 Days</option>
                    <option value="last365days">Last 365 Days</option>
                    <option value="alltime">All Time</option>
                  </select>

                  <select
                    className={styles.inlineFilterSelect}
                    value={filters.eloRange}
                    onChange={(e) => setFilters({...filters, eloRange: e.target.value as EloRangeFilter})}
                  >
                    <option value="all">All Players</option>
                    <option value="unranked">Unranked - New Accounts</option>
                    <option value="below1000">&lt;1000 - Beginner</option>
                    <option value="1000-1200">1000-1200 - Developing</option>
                    <option value="1200-1400">1200-1400 - Intermediate</option>
                    <option value="1400-1600">1400-1600 - Advanced</option>
                    <option value="1600-1800">1600-1800 - Expert</option>
                    <option value="1800-2000">1800-2000 - Master</option>
                    <option value="2000plus">2000+ - Grandmaster</option>
                  </select>

                  <select
                    className={styles.inlineFilterSelect}
                    value={filters.region}
                    onChange={(e) => setFilters({...filters, region: e.target.value as RegionFilter})}
                  >
                    <option value="all">All Regions</option>
                    <option value="NA">North America</option>
                    <option value="SA">South America</option>
                    <option value="EU">Europe</option>
                    <option value="AF">Africa</option>
                    <option value="AS">Asia</option>
                    <option value="OC">Oceania</option>
                  </select>

                  <select
                    className={styles.inlineFilterSelect}
                    value={filters.color}
                    onChange={(e) => setFilters({...filters, color: e.target.value as ColorFilter})}
                  >
                    <option value="all">Both Colors</option>
                    <option value="light">Light</option>
                    <option value="dark">Dark</option>
                  </select>
                </div>
              </div>

              <div className={styles.tableWrapper}>
                <table className={styles.statsTable}>
                  <thead>
                    <tr>
                      <th className={styles.stickyColumn} onClick={() => handleSort('piece', kingStats, setKingStats)}>
                        King {renderSortIcon('piece')}
                      </th>
                      <th onClick={() => { setKingSortCol('kingPickRate'); handleSort('kingPickRate', kingStats, setKingStats); }}>
                        Pick Rate {renderSortIcon('kingPickRate')}
                        <span className={styles.columnHint}>% chosen as king</span>
                      </th>
                      <th onClick={() => { setKingSortCol('wins'); handleSort('wins', kingStats, setKingStats); }}>
                        Wins {renderSortIcon('wins')}
                        <span className={styles.columnHint}>Total wins</span>
                      </th>
                      <th onClick={() => { setKingSortCol('losses'); handleSort('losses', kingStats, setKingStats); }}>
                        Losses {renderSortIcon('losses')}
                        <span className={styles.columnHint}>Total losses</span>
                      </th>
                      <th onClick={() => { setKingSortCol('winRateAsKing'); handleSort('winRateAsKing', kingStats, setKingStats); }}>
                        Win Rate {renderSortIcon('winRateAsKing')}
                        <span className={styles.columnHint}>% wins as king</span>
                      </th>
                      <th onClick={() => { setKingSortCol('raceVictoryRate'); handleSort('raceVictoryRate', kingStats, setKingStats); }}>
                        Race Win % {renderSortIcon('raceVictoryRate')}
                        <span className={styles.columnHint}>% of wins by race</span>
                      </th>
                      <th onClick={() => { setKingSortCol('captureDeathRate'); handleSort('captureDeathRate', kingStats, setKingStats); }}>
                        Captured % {renderSortIcon('captureDeathRate')}
                        <span className={styles.columnHint}>% of losses by capture</span>
                      </th>
                      <th onClick={() => { setKingSortCol('gamesAsKing'); handleSort('gamesAsKing', kingStats, setKingStats); }}>
                        Games {renderSortIcon('gamesAsKing')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {kingStats.map((stat) => (
                      <tr key={stat.piece}>
                        <td className={styles.stickyColumn}>
                          <div className={styles.pieceCell}>
                            {filters.color === 'all' ? (
                              <div className={styles.pieceBothIcons}>
                                <img src={stat.icon.replace('-light', '-dark')} alt={`${stat.piece} Dark`} className={styles.pieceIconDark} />
                                <img src={stat.icon} alt={`${stat.piece} Light`} className={styles.pieceIconLight} />
                              </div>
                            ) : filters.color === 'dark' ? (
                              <img src={stat.icon.replace('-light', '-dark')} alt={stat.piece} className={styles.pieceIcon} />
                            ) : (
                              <img src={stat.icon} alt={stat.piece} className={styles.pieceIcon} />
                            )}
                            <span className={styles.pieceName}>{stat.piece}</span>
                          </div>
                        </td>
                        <td>
                          <div className={styles.barCell}>
                            <div className={styles.bar} style={{ width: `${stat.kingPickRate * 5}%`, backgroundColor: getBarColor(stat.kingPickRate * 5, 'popularity') }} />
                            <span className={styles.barValue}>{stat.kingPickRate}%</span>
                          </div>
                        </td>
                        <td><div className={styles.gamesCell}>{stat.wins}</div></td>
                        <td><div className={styles.gamesCell}>{stat.losses}</div></td>
                        <td>
                          <div className={styles.barCell}>
                            <div className={styles.bar} style={{ width: `${stat.winRateAsKing}%`, backgroundColor: getBarColor(stat.winRateAsKing, 'winRateAsKing') }} />
                            <span className={styles.barValue}>{stat.winRateAsKing}%</span>
                          </div>
                        </td>
                        <td>
                          <div className={styles.barCell}>
                            <div className={styles.bar} style={{ width: `${stat.raceVictoryRate}%`, backgroundColor: '#60a5fa' }} />
                            <span className={styles.barValue}>{stat.raceVictoryRate}%</span>
                          </div>
                        </td>
                        <td>
                          <div className={styles.barCell}>
                            <div className={styles.bar} style={{ width: `${stat.captureDeathRate}%`, backgroundColor: '#f87171' }} />
                            <span className={styles.barValue}>{stat.captureDeathRate}%</span>
                          </div>
                        </td>
                        <td><div className={styles.gamesCell}>{stat.gamesAsKing.toLocaleString()}</div></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* Game Statistics Tab */}
        {activeTab === 'games' && (
          <>
            <div className={styles.statsOverview}>
              <div className={styles.statCard}>
                <div className={styles.statValue}>{gameStats[0].gamesPlayed.toLocaleString()}</div>
                <div className={styles.statLabel}>Total Games</div>
              </div>
              <div className={styles.statCard}>
                <div className={styles.statValue}>{gameStats[0].avgMovesPerGame}</div>
                <div className={styles.statLabel}>Avg Moves Per Game</div>
              </div>
              <div className={styles.statCard}>
                <div className={styles.statValue}>{gameStats[0].avgCapturesPerGame.toFixed(1)}</div>
                <div className={styles.statLabel}>Avg Captures Per Game</div>
              </div>
              <div className={styles.statCard}>
                <div className={styles.statValue}>{gameStats[0].raceVictoryRate}%</div>
                <div className={styles.statLabel}>Race Victories</div>
              </div>
              <div className={styles.statCard}>
                <div className={styles.statValue}>{gameStats[0].captureVictoryRate}%</div>
                <div className={styles.statLabel}>Capture Victories</div>
              </div>
            </div>

            <div className={styles.tableContainer}>
              <div className={styles.tableHeader}>
                <div className={styles.tableHeaderLeft}>
                  <h2>Game Statistics</h2>
                  <div className={styles.tableInfo}>Detailed breakdown of game metrics</div>
                </div>

                {/* Inline Filter Dropdowns - No color filter for Game Statistics */}
                <div className={styles.tableHeaderFilters}>
                  <select
                    className={styles.inlineFilterSelect}
                    value={filters.timeframe}
                    onChange={(e) => setFilters({...filters, timeframe: e.target.value as TimeframeFilter})}
                  >
                    <option value="last30days">Last 30 Days</option>
                    <option value="last365days">Last 365 Days</option>
                    <option value="alltime">All Time</option>
                  </select>

                  <select
                    className={styles.inlineFilterSelect}
                    value={filters.eloRange}
                    onChange={(e) => setFilters({...filters, eloRange: e.target.value as EloRangeFilter})}
                  >
                    <option value="all">All Players</option>
                    <option value="unranked">Unranked - New Accounts</option>
                    <option value="below1000">&lt;1000 - Beginner</option>
                    <option value="1000-1200">1000-1200 - Developing</option>
                    <option value="1200-1400">1200-1400 - Intermediate</option>
                    <option value="1400-1600">1400-1600 - Advanced</option>
                    <option value="1600-1800">1600-1800 - Expert</option>
                    <option value="1800-2000">1800-2000 - Master</option>
                    <option value="2000plus">2000+ - Grandmaster</option>
                  </select>

                  <select
                    className={styles.inlineFilterSelect}
                    value={filters.region}
                    onChange={(e) => setFilters({...filters, region: e.target.value as RegionFilter})}
                  >
                    <option value="all">All Regions</option>
                    <option value="NA">North America</option>
                    <option value="SA">South America</option>
                    <option value="EU">Europe</option>
                    <option value="AF">Africa</option>
                    <option value="AS">Asia</option>
                    <option value="OC">Oceania</option>
                  </select>
                </div>
              </div>

              <div className={styles.tableWrapper}>
                <table className={styles.statsTable}>
                  <thead>
                    <tr>
                      <th className={styles.stickyColumn}>
                        Category
                      </th>
                      <th>
                        Avg Moves
                        <span className={styles.columnHint}>Total moves per game</span>
                      </th>
                      <th>
                        Avg Captures
                        <span className={styles.columnHint}>Pieces captured per game</span>
                      </th>
                      <th>
                        Race Victory %
                        <span className={styles.columnHint}>King reaches end</span>
                      </th>
                      <th>
                        Capture Victory %
                        <span className={styles.columnHint}>King captured</span>
                      </th>
                      <th>
                        Draw Rate %
                        <span className={styles.columnHint}>% draws</span>
                      </th>
                      <th>
                        Victory Ratio
                        <span className={styles.columnHint}>Race : Capture : Draw</span>
                      </th>
                      <th>
                        Games Played
                        <span className={styles.columnHint}>Sample size</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {gameStats.map((stat, index) => (
                      <tr key={index}>
                        <td className={styles.stickyColumn}>
                          <div className={styles.categoryCell}>
                            <span className={styles.categoryName}>{stat.category}</span>
                          </div>
                        </td>
                        <td>
                          <div className={styles.valueCell}>
                            <span className={styles.lifespanValue}>{stat.avgMovesPerGame}</span>
                            <span className={styles.lifespanUnit}>moves</span>
                          </div>
                        </td>
                        <td>
                          <div className={styles.valueCell}>
                            <span className={styles.lifespanValue}>{stat.avgCapturesPerGame.toFixed(1)}</span>
                            <span className={styles.lifespanUnit}>pieces</span>
                          </div>
                        </td>
                        <td>
                          <div className={styles.barCell}>
                            <div className={styles.bar} style={{ width: `${stat.raceVictoryRate}%`, backgroundColor: '#60a5fa' }} />
                            <span className={styles.barValue}>{stat.raceVictoryRate}%</span>
                          </div>
                        </td>
                        <td>
                          <div className={styles.barCell}>
                            <div className={styles.bar} style={{ width: `${stat.captureVictoryRate}%`, backgroundColor: '#f87171' }} />
                            <span className={styles.barValue}>{stat.captureVictoryRate}%</span>
                          </div>
                        </td>
                        <td>
                          <div className={styles.barCell}>
                            <div className={styles.bar} style={{ width: `${stat.drawRate}%`, backgroundColor: '#fbbf24' }} />
                            <span className={styles.barValue}>{stat.drawRate}%</span>
                          </div>
                        </td>
                        <td>
                          <div className={styles.valueCell}>
                            <span className={styles.lifespanValue}>
                              {stat.raceVictoryRate.toFixed(1)} : {stat.captureVictoryRate.toFixed(1)} : {stat.drawRate.toFixed(1)}
                            </span>
                          </div>
                        </td>
                        <td><div className={styles.gamesCell}>{stat.gamesPlayed.toLocaleString()}</div></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Light vs Dark Comparison */}
            <div className={styles.tableContainer}>
              <div className={styles.tableHeader}>
                <div className={styles.tableHeaderLeft}>
                  <h2>Light vs Dark Detailed Metrics</h2>
                  <div className={styles.tableInfo}>Comparative analysis of color performance</div>
                </div>
              </div>

              <div className={styles.tableWrapper}>
                <table className={styles.statsTable}>
                  <thead>
                    <tr>
                      <th className={styles.stickyColumn}>
                        Color
                      </th>
                      <th>
                        Win Rate
                        <span className={styles.columnHint}>% games won</span>
                      </th>
                      <th>
                        Avg Captures
                        <span className={styles.columnHint}>Pieces captured</span>
                      </th>
                      <th>
                        First Capture %
                        <span className={styles.columnHint}>Makes first capture</span>
                      </th>
                      <th>
                        Center Control
                        <span className={styles.columnHint}>Avg score in B3-D6</span>
                      </th>
                      <th>
                        Race Victory %
                        <span className={styles.columnHint}>King reaches end</span>
                      </th>
                      <th>
                        Capture Victory %
                        <span className={styles.columnHint}>King captured</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {colorComparison.map((stat) => (
                      <tr key={stat.color}>
                        <td className={styles.stickyColumn}>
                          <div className={styles.pieceCell}>
                            <img src={stat.icon} alt={stat.color} className={styles.pieceIcon} />
                            <span className={styles.pieceName}>{stat.color}</span>
                          </div>
                        </td>
                        <td>
                          <div className={styles.barCell}>
                            <div className={styles.bar} style={{ width: `${stat.winRate}%`, backgroundColor: '#4ade80' }} />
                            <span className={styles.barValue}>{stat.winRate}%</span>
                          </div>
                        </td>
                        <td>
                          <div className={styles.valueCell}>
                            <span className={styles.lifespanValue}>{stat.avgCapturesPerGame.toFixed(1)}</span>
                            <span className={styles.lifespanUnit}>pieces</span>
                          </div>
                        </td>
                        <td>
                          <div className={styles.barCell}>
                            <div className={styles.bar} style={{ width: `${stat.firstCaptureRate}%`, backgroundColor: '#a78bfa' }} />
                            <span className={styles.barValue}>{stat.firstCaptureRate}%</span>
                          </div>
                        </td>
                        <td>
                          <div className={styles.valueCell}>
                            <span className={styles.lifespanValue}>{stat.centerControlScore.toFixed(1)}</span>
                            <span className={styles.lifespanUnit}>pts</span>
                          </div>
                        </td>
                        <td>
                          <div className={styles.barCell}>
                            <div className={styles.bar} style={{ width: `${stat.raceVictoryRate}%`, backgroundColor: '#60a5fa' }} />
                            <span className={styles.barValue}>{stat.raceVictoryRate}%</span>
                          </div>
                        </td>
                        <td>
                          <div className={styles.barCell}>
                            <div className={styles.bar} style={{ width: `${stat.captureVictoryRate}%`, backgroundColor: '#f87171' }} />
                            <span className={styles.barValue}>{stat.captureVictoryRate}%</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        <div className={styles.statusBar}>
          <span className={styles.statusText}>Testing Mode - Analytics Unlocked for All</span>
          <span className={styles.statusText}>Piece & King data from 12 alpha games</span>
          <span className={styles.statusText}>Updates as data grows</span>
        </div>
      </div>

      {/* Export Modal */}
      {showExportModal && (
        <div className={styles.modalOverlay} onClick={() => setShowExportModal(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h2 className={styles.modalTitle}>Export Game Data</h2>
            <p className={styles.modalDescription}>
              Select a month to export completed games in JSON format
            </p>

            <div className={styles.formGroup}>
              <label className={styles.label}>Select Month:</label>
              <select
                className={styles.dropdown}
                value={selectedExportMonth}
                onChange={(e) => setSelectedExportMonth(e.target.value)}
              >
                {availableMonths.map(monthData => (
                  <option key={monthData.month} value={monthData.month}>
                    {monthData.month} ({monthData.count} {monthData.count === 1 ? 'game' : 'games'})
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.modalActions}>
              <button
                className={styles.cancelButton}
                onClick={() => setShowExportModal(false)}
              >
                Cancel
              </button>
              <button
                className={styles.confirmButton}
                onClick={handleExportGames}
              >
                Export
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Analytics;
